import { useCallback, useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useAccessCan } from "@/modules/dashboard/settings/hooks/useAccessCan";
import { useRolesData } from "@/modules/dashboard/settings/hooks/useAccessData";
import type { AccessRole } from "@/modules/dashboard/settings/types/access.types";

import {
  ACCESS_FILTER_ALL,
  ACCESS_TABLE_PAGE_SIZE,
  type AccessScopeFilter,
} from "../constants/access.constants";
import { matchesScope, normalizeSearchTerm } from "../utils/catalogFilters";
import { clampPage, pageSlice } from "../utils/pageSlice";

/** The four buckets of the KPI strip. The strip IS the filter, so each key is also a filter value. */
export type RolesBucket = typeof ACCESS_FILTER_ALL | "deny" | "no_policies" | "no_members";

export interface RoleRow {
  role: AccessRole;
  hasDeny: boolean;
  /** Not protected AND in the caller's scope — the client-side mirror of the backend's `assertWritable`. */
  editable: boolean;
}

export type RolesBucketCounts = Record<RolesBucket, number>;

/**
 * Which bucket a row belongs to. Exported for its test — the repo covers hook logic by pulling the pure part
 * out of the hook (see `chunkStatementTransactions`), since nothing here renders one.
 */
export function matchesRoleBucket(row: RoleRow, bucket: RolesBucket): boolean {
  if (bucket === "deny") return row.hasDeny;
  if (bucket === "no_policies") return row.role.policies.length === 0;
  if (bucket === "no_members") return row.role.member_count === 0;
  return true;
}

/**
 * Name, **slug** and description. The slug is not padding: the seeded roles are found by slug (`cards_read`)
 * far more often than by their title-cased name, and an operator pasting one out of an audit log expects it
 * to hit.
 *
 * `term` is normalized by the caller; normalizing again here is cheap and keeps the function usable alone.
 */
export function matchesRoleSearch(role: AccessRole, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return (
    role.name.toLowerCase().includes(needle) ||
    role.slug.toLowerCase().includes(needle) ||
    (role.description?.toLowerCase().includes(needle) ?? false)
  );
}

/**
 * The roles catalog as the page consumes it: search, scope, bucket, pagination and the permission
 * affordances, over the shared catalog the provider already holds.
 *
 * Everything is derived in memory because the endpoint offers nothing else (see `access.constants.ts`), and
 * the catalog is loaded whole — so the counts and the pagination describe every role that exists, not the
 * slice a server page happened to return.
 *
 * The bucket counts are computed AFTER search and scope and BEFORE the bucket itself, mirroring how
 * `/internal/users/counts` honours `search` while each tile still reports its own bucket. Selecting a tile
 * therefore narrows the table without making the other tiles lie about what they would show.
 */
export function useRolesCatalog() {
  const { isInternalUser } = useAuth();
  const { canAll } = useAccessCan();
  const { roles, isLoading, hasNoAccess, load } = useRolesData();

  const [query, setQueryState] = useState("");
  const [scope, setScopeState] = useState<AccessScopeFilter>(ACCESS_FILTER_ALL);
  const [bucket, setBucketState] = useState<RolesBucket>(ACCESS_FILTER_ALL);
  const [page, setPage] = useState(1);

  // Page resets live in the SETTER, never in an effect: an effect would first render page 4 of a 1-page
  // result — an empty table under a total that says otherwise.
  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    setPage(1);
  }, []);
  const setScope = useCallback((next: AccessScopeFilter) => {
    setScopeState(next);
    setPage(1);
  }, []);
  const setBucket = useCallback((next: RolesBucket) => {
    setBucketState(next);
    setPage(1);
  }, []);

  /**
   * The client-side mirror of the backend's `assertWritable`: org members can only touch their own
   * organization's roles, staff can also edit global ones, and a protected role is read-only for everyone
   * (the 86 seeded ones are all protected, so this is the common case, not the exception).
   */
  const isEditable = useCallback(
    (role: AccessRole): boolean => !role.is_protected && (isInternalUser || !!role.organization_id),
    [isInternalUser],
  );

  const rows = useMemo<RoleRow[]>(
    () =>
      roles.map((role) => ({
        role,
        hasDeny: role.policies.some((policy) => policy.effect === "deny"),
        editable: isEditable(role),
      })),
    [roles, isEditable],
  );

  const scoped = useMemo(() => {
    const term = normalizeSearchTerm(query);
    return rows.filter(
      ({ role }) => matchesScope(role.organization_id, scope) && matchesRoleSearch(role, term),
    );
  }, [rows, query, scope]);

  const counts = useMemo<RolesBucketCounts>(
    () => ({
      all: scoped.length,
      deny: scoped.filter((row) => matchesRoleBucket(row, "deny")).length,
      no_policies: scoped.filter((row) => matchesRoleBucket(row, "no_policies")).length,
      no_members: scoped.filter((row) => matchesRoleBucket(row, "no_members")).length,
    }),
    [scoped],
  );

  const filtered = useMemo(
    () => (bucket === ACCESS_FILTER_ALL ? scoped : scoped.filter((row) => matchesRoleBucket(row, bucket))),
    [scoped, bucket],
  );

  const safePage = clampPage(page, filtered.length);
  const paged = useMemo(() => pageSlice(filtered, page), [filtered, page]);

  const hasActiveFilters = query.trim() !== "" || scope !== ACCESS_FILTER_ALL || bucket !== ACCESS_FILTER_ALL;

  const clearFilters = useCallback(() => {
    setQueryState("");
    setScopeState(ACCESS_FILTER_ALL);
    setBucketState(ACCESS_FILTER_ALL);
    setPage(1);
  }, []);

  return {
    rows: paged,
    total: filtered.length,
    counts,
    isLoading,
    hasNoAccess,
    reload: load,
    page: safePage,
    pageSize: ACCESS_TABLE_PAGE_SIZE,
    setPage,
    query,
    setQuery,
    scope,
    setScope,
    bucket,
    setBucket,
    hasActiveFilters,
    clearFilters,
    isEditable,
    // `:write`, not `:update` — `scopes[]` carries RBAC atoms and the backend guard collapses `update` to
    // `write`, so checking the composite would always be false.
    canCreate: canAll("roles:write"),
    canUpdate: canAll("roles:write"),
    canDelete: canAll("roles:delete"),
  };
}
